/**
 * Curated related-domain equivalence groups. Domains in the same group are
 * treated as the same site for PASSWORD-LOGIN matching only (never passkeys).
 *
 * HAND-VETTED ONLY — never heuristic, never auto-learned. A saved login is
 * offered on another domain only if we have explicitly confirmed the two are
 * the same account system. Over-broad groups weaken the anti-phishing
 * guarantee. Entries are registrable domains (eTLD+1), lowercase.
 *
 * This is the cross-client source of truth. When you change the groups, also update the
 * hand-ported copies: android/core/domain/src/main/kotlin/com/heybesafe/domain/RelatedDomains.kt
 * and ios/Packages/HBSCore/Sources/HBSDomain/RelatedDomains.swift.
 */
export const RELATED_DOMAIN_GROUPS: readonly (readonly string[])[] = [
  ['wsj.com', 'dowjones.com', 'barrons.com', 'marketwatch.com'], // Dow Jones family (confirmed)
  ['max.com', 'hbomax.com'],                                     // HBO Max rebrand (confirmed)
  // Condé Nast (confirmed 2026-08-25). Every title below sends its sign-in to
  // id.condenast.com, serving the same form (field id `TextField-id-email`);
  // one credential, with separate SUBSCRIPTIONS deciding which title's content
  // you can read. The title domains are here because the case that needs them
  // is an item saved for the TITLE (imported, or saved before the SSO
  // migration) not being offered at the IdP.
  //
  // NOT members, deliberately, both verified by probing their sign-in:
  //   arstechnica.com — Condé Nast owns it, but it runs its own login
  //                     (XenForo, field `_xfUid-…`). Ownership is not a login system.
  //   wmagazine.com   — no Condé Nast sign-in; the title left the group.
  //   them.us         — no /auth/initiate; unverified, so left out.
  [
    'condenast.com',
    'vogue.com', 'gq.com', 'vanityfair.com', 'architecturaldigest.com',
    'bonappetit.com', 'newyorker.com', 'self.com', 'allure.com',
    'epicurious.com', 'pitchfork.com', 'wired.com', 'cntraveler.com',
    'glamour.com', 'teenvogue.com',
  ],
  // Public media (confirmed 2026-08-25). npr.org's sign-in lands on
  // login.publicmediasignin.org — PBS's Public Media SSO, one credential across
  // NPR, PBS, participating stations and PRX.
  //
  // The IdP ONLY. Member-station domains are deliberately absent: stations that
  // use PM SSO send the user to publicmediasignin.org to type the credential, so
  // they are already covered, and stations that DON'T (kqed.org and wnyc.org both
  // host their own login forms on their own domains) would be offered an NPR
  // credential on an unrelated account system. A long station list would be both
  // unnecessary and wrong.
  ['npr.org', 'publicmediasignin.org'],
  // Candidates observed in harness testing — add ONLY after verifying the same
  // login actually works across all members:
  //   microsoft.com / microsoftonline.com   (consumer vs AAD/org — verify)
  //   playstation.com / sony.com            (verify)
  //   Disney / MyDisney (OneID): espn.com, go.com, disney.com, disneyplus.com
  //     Observed 2026-09-27 in the tier-2 sweep. espn.com's account popup opens
  //     the MyDisney overlay from cdn.registerdisney.go.com (eTLD+1 go.com) —
  //     a DIFFERENT registrable domain, so an espn.com item is never offered
  //     there. The gap is invisible to the harness: the form is inside a
  //     cross-origin iframe (no marks) AND the top-frame URL stays espn.com
  //     (so not even DOMAIN_SHIFT). disney.com and disneyplus.com both embed a
  //     login.disney.com bridge iframe, which is why they are listed with it.
  //     TO PROMOTE: sign in at espn.com with a real MyDisney account and confirm
  //     the SAME credential is accepted at disneyplus.com — probing form hosts
  //     is not proof of a shared credential store.
  //   NOT a candidate: hulu.com. Disney owns it, but hulu.com/login redirects to
  //     auth.hulu.com and serves its own form (field `email-field`), the same
  //     reason arstechnica.com is excluded from the Condé Nast group above.
];

// domain -> set of all domains in its group (including itself). Built once.
const groupIndex: ReadonlyMap<string, ReadonlySet<string>> = (() => {
  const index = new Map<string, ReadonlySet<string>>();
  for (const group of RELATED_DOMAIN_GROUPS) {
    const set = new Set(group.map((d) => d.toLowerCase()));
    for (const d of set) index.set(d, set);
  }
  return index;
})();

/** All domains in `domain`'s group (incl. itself), or just `[domain]` when ungrouped. */
export function relatedDomains(domain: string): string[] {
  const set = groupIndex.get(domain.toLowerCase());
  return set ? [...set] : [domain.toLowerCase()];
}

/** Symmetric: true when a === b, or a and b share a curated group. Case-insensitive. */
export function areRelatedDomains(a: string, b: string): boolean {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  if (x === y) return true;
  const set = groupIndex.get(x);
  return set ? set.has(y) : false;
}
